"use client";

import * as React from "react";
import * as CheckboxPrimitive from "@radix-ui/react-checkbox";
import { cva, type VariantProps } from "class-variance-authority";
import { Check, Minus } from "lucide-react";

import { cn } from "../../lib/utils";

const checkboxVariants = cva(
	"peer shrink-0 bg-background border border-neutral-300 rounded outline-none focus-visible:ring-2 focus-visible:ring-ring/50 focus-visible:ring-offset-2 group-hover:data-[state=unchecked]:border-neutral-400 disabled:cursor-not-allowed disabled:opacity-50 aria-[invalid=true]:bg-danger-50 aria-[invalid=true]:border-2 aria-[invalid=true]:border-danger data-[state=checked]:aria-[invalid=true]:bg-danger-300 data-[state=checked]:aria-[invalid=true]:border-danger-300 data-[state=checked]:bg-success data-[state=checked]:border-success data-[state=indeterminate]:bg-success data-[state=indeterminate]:border-success",
	{
		variants: {
			size: {
				sm: "h-[1.1rem] w-[1.1rem]",
				md: "h-5 w-5",
			},
		},
		defaultVariants: {
			size: "md",
		},
	},
);

/** Styled checkbox built on Radix `CheckboxPrimitive.Root`. Forwards all primitive props and ref. */
const Checkbox = React.forwardRef<
	React.ElementRef<typeof CheckboxPrimitive.Root>,
	React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root> &
		VariantProps<typeof checkboxVariants>
>(({ className, size, ...props }, ref) => (
	<CheckboxPrimitive.Root
		ref={ref}
		className={cn(checkboxVariants({ size }), className)}
		{...props}
	>
		<CheckboxPrimitive.Indicator
			forceMount
			className="flex items-center justify-center text-current data-[state=unchecked]:opacity-0"
		>
			{props.checked === "indeterminate" ? (
				<Minus className="size-3.5 stroke-control-mark stroke-[3px]" />
			) : (
				<Check className="size-3.5 stroke-control-mark stroke-[3px]" />
			)}
		</CheckboxPrimitive.Indicator>
	</CheckboxPrimitive.Root>
));
Checkbox.displayName = CheckboxPrimitive.Root.displayName;

/**
 * Props for the {@link CheckboxCard} component.
 *
 * Extends all Radix `Checkbox.Root` props.
 * @property label - Required visible label rendered next to the checkbox.
 */
type CheckboxCardProps = React.ComponentPropsWithoutRef<
	typeof CheckboxPrimitive.Root
> & {
	label: string;
};

/** Checkbox wrapped in a bordered card-style label. */
const CheckboxCard = React.forwardRef<
	React.ElementRef<typeof CheckboxPrimitive.Root>,
	CheckboxCardProps
>(({ className, label, id, ...props }, ref) => {
	const generatedId = React.useId();
	const checkboxId = id ?? generatedId;
	return (
		<label
			htmlFor={checkboxId}
			className={cn(
				"bg-background flex cursor-pointer items-center gap-3 rounded-md border border-neutral-200 p-3 transition-colors hover:bg-neutral-50 has-[:disabled]:cursor-not-allowed has-[:disabled]:opacity-50 dark:border-neutral-800 dark:hover:bg-neutral-900",
				"has-[[data-state=checked]]:border-neutral-900 dark:has-[[data-state=checked]]:border-neutral-50 dark:has-[[data-state=checked]]:bg-neutral-900",
				className,
			)}
		>
			<Checkbox ref={ref} id={checkboxId} {...props} />
			{/* leading-snug matches RadioGroupCardItem. At leading-loose a wrapped
				    option read as two separate items rather than one sentence. */}
			<span className="text-sm font-medium leading-snug">{label}</span>
		</label>
	);
});
CheckboxCard.displayName = "CheckboxCard";

export { Checkbox, CheckboxCard };
